import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from 'vue'
import { type Routes } from '@/router/routes'
import type { Breadcrumb } from '@/components/ui/Breadcrumbs.vue'
import type { MetalCode, WeddingRingCategoryLabelCode } from '@/graphql/api'
import { type WeddingRingStyleLabelCode } from '@/graphql/api'

type Label<I extends string> = {
    systemCode: I
    translation: {
        name: string
    }
}

export function getWeddingRingBreadcrumbs(
    data: MaybeRefOrGetter<{
        category: MaybeRefOrGetter<
            Label<WeddingRingCategoryLabelCode> | undefined
        >
        metal: MaybeRefOrGetter<Label<MetalCode> | undefined>
        ringStyle: MaybeRefOrGetter<
            Label<WeddingRingStyleLabelCode> | undefined
        >
        product: MaybeRefOrGetter<Breadcrumb | undefined> | undefined
    }>,
    routes: Routes
): ComputedRef<Breadcrumb[]> {
    return computed<Breadcrumb[]>(() => {
        const { category, metal, ringStyle, product } = toValue(data)

        const baseBreadcrumbs: Breadcrumb[] = [
            {
                title: 'Home',
                to: routes.home(),
            },
            {
                title: 'Wedding Rings',
                to: routes.weddingRings(),
            },
        ]

        const categoryValue = toValue(category)
        if (categoryValue) {
            baseBreadcrumbs.push({
                title: categoryValue.translation?.name,
                to: routes.weddingRings({
                    category: categoryValue.systemCode,
                }),
            })
        }

        const metalValue = toValue(metal)
        if (metalValue) {
            baseBreadcrumbs.push({
                title: metalValue.translation.name,
                to: routes.weddingRings({
                    metal: metalValue.systemCode,
                }),
            })
        }

        const ringStyleValue = toValue(ringStyle)
        if (ringStyleValue) {
            baseBreadcrumbs.push({
                title: ringStyleValue.translation?.name,
                to: routes.weddingRings({
                    ringStyle: ringStyleValue.systemCode,
                }),
            })
        }

        const productValue = toValue(product)
        if (productValue) {
            baseBreadcrumbs.push(productValue)
        }

        return baseBreadcrumbs
    })
}
